import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import "../styles/EditStory.css";

export default function EditStory() {
  const navigate = useNavigate();
  const location = useLocation();
  const editorRef = useRef(null);

  const [bookTitle, setBookTitle] = useState(location.state?.title || "Adsız Bölüm 1");
  const [bookId] = useState(location.state?.bookId);
  const [chapterId, setChapterId] = useState(location.state?.chapterId);
  const [isPublished, setIsPublished] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [wordCount, setWordCount] = useState(0); // New state for word count

  // formatting states
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const MAX_CHARACTERS = 100000;

  // New function to calculate word count
  const calculateWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    // Splits by any whitespace character and filters out empty strings
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const formatContentWithParagraphs = (content) => {
    if (!content) return "";
    if (content.includes("<p>") || content.includes("<br>") || content.includes("<div>")) {
      return content;
    }
    return content
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => `<p>${line}</p>`)
      .join("");
  };

  const formatEditorContent = () => {
    if (!editorRef.current) return;
    const content = editorRef.current.innerHTML;
    if (!content.includes("<p>") && !content.includes("<br>") && content.trim()) {
      const formattedContent = formatContentWithParagraphs(editorRef.current.textContent);
      editorRef.current.innerHTML = formattedContent;
    }
  };

  useEffect(() => {
    if (!bookId) return;

    const fetchChapter = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await api.get(`/stories/chapters/${chapterId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (editorRef.current && res.data.content) {
          const formattedContent = formatContentWithParagraphs(res.data.content);
          editorRef.current.innerHTML = formattedContent;
          // Calculate word count on initial load
          setWordCount(calculateWordCount(editorRef.current.textContent));
        }

        setBookTitle(res.data.title || "Adsız Bölüm 1");
        setIsPublished(res.data.is_published || false);
      } catch (err) {
        console.error("Bölüm getirirken hata:", err);
      }
    };

    if (chapterId) fetchChapter();
  }, [bookId, chapterId]);

  useEffect(() => {
    const handleSelectionChange = () => {
      setIsBold(document.queryCommandState("bold"));
      setIsItalic(document.queryCommandState("italic"));
      setIsStrikethrough(document.queryCommandState("strikeThrough"));
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const newParagraph = document.createElement("p");
      newParagraph.innerHTML = "<br>";
      range.deleteContents();
      range.insertNode(newParagraph);
      range.setStart(newParagraph, 0);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      formatEditorContent();
    }
  };

  /**
   * Ortak kayıt fonksiyonu.
   * Hem kaydet hem önizleme hem publish bunu kullanır.
   * @returns chapter_id veya null
   */
  const saveChapter = async () => {
    formatEditorContent();
    const htmlContent = editorRef.current?.innerHTML.trim();
    const textContent = editorRef.current?.textContent.trim();

    if (!bookTitle.trim() || !textContent) {
      alert("Başlık ve içerik boş olamaz.");
      return null;
    }

    try {
      const token = localStorage.getItem("token");
      const chapterData = {
        book_id: bookId,
        chapter_id: chapterId,
        title: bookTitle.trim(),
        content: htmlContent,
        is_published: isPublished,
      };

      const res = await api.post("/stories/chapters", chapterData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setChapterId(res.data.chapter_id);
      setSuccessMessage("Bölüm kaydedildi.");
      setTimeout(() => setSuccessMessage(""), 3000);
      return res.data.chapter_id;
    } catch (err) {
      console.error(err);
      alert("Kaydedilirken bir hata oluştu.");
      return null;
    }
  };

  const handleSave = async () => {
    await saveChapter();
  };

  const handlePreview = async () => {
    const savedChapterId = await saveChapter();

    if (!savedChapterId) {
      alert("Kaydedilemedi. Önizleme yapılamıyor.");
      return;
    }

    navigate("/preview", {
      state: {
        title: bookTitle,
        content: editorRef.current?.innerHTML,
        storyId: bookId,
        chapterId: savedChapterId,
      },
    });
  };

  const handlePublishToggle = () => setIsModalOpen(true);

  const handleConfirmPublish = async () => {
    setIsModalOpen(false);
    formatEditorContent();
    const htmlContent = editorRef.current?.innerHTML.trim();
    const textContent = editorRef.current?.textContent.trim();

    if (!bookTitle.trim() || !textContent) {
      alert("Başlık ve içerik boş olamaz.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const chapterData = {
        book_id: bookId,
        chapter_id: chapterId,
        title: bookTitle.trim(),
        content: htmlContent,
        is_published: !isPublished,
      };
      const res = await api.post("/stories/chapters", chapterData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChapterId(res.data.chapter_id);
      setIsPublished(!isPublished);
      setSuccessMessage(isPublished ? "Yayımdan kaldırıldı." : "Yayımlandı.");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error(err);
      alert("Yayımlanırken bir hata oluştu.");
    }
  };

  const toggleBold = () => {
    document.execCommand("bold");
    editorRef.current?.focus();
  };
  const toggleItalic = () => {
    document.execCommand("italic");
    editorRef.current?.focus();
  };
  const toggleStrikethrough = () => {
    document.execCommand("strikeThrough");
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (!editorRef.current) return;
    const textContent = editorRef.current.textContent || "";
    setWordCount(calculateWordCount(textContent)); // Update word count on every input

    if (textContent.length > MAX_CHARACTERS) {
      alert("Karakter sınırını aştınız!");
      document.execCommand("undo");
      // Recalculate after undo
      setWordCount(calculateWordCount(editorRef.current.textContent));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const formattedText = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => `<p>${line}</p>`)
      .join("");

    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    range.deleteContents();

    const fragment = document.createDocumentFragment();
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = formattedText;
    while (tempDiv.firstChild) {
      fragment.appendChild(tempDiv.firstChild);
    }
    range.insertNode(fragment);
    
    // Manually trigger word count update after paste
    if(editorRef.current) {
        setWordCount(calculateWordCount(editorRef.current.textContent));
    }
    
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const handleGoBack = () => navigate(-1);

  if (!bookId) return null;

  return (
    <div className="edit-story-container">
      <header className="edit-story-header">
        <div className="header-left">
          <button className="back-button" onClick={handleGoBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="page-title">Bölüm Düzenle</h1>
        </div>
        <div className="header-actions">
          <button className="action-btn save-btn" onClick={handleSave}>
            💾 <span>Kaydet</span>
          </button>
          <button className="action-btn preview-btn" onClick={handlePreview}>
            👁 <span>Önizle</span>
          </button>
          <button
            className={`action-btn publish-btn ${isPublished ? "published" : ""}`}
            onClick={handlePublishToggle}
          >
            🚀 <span>{isPublished ? "Yayımdan Kaldır" : "Yayımla"}</span>
          </button>
        </div>
      </header>

      <main className="edit-story-main">
        <div className="editor-container">
          <div className="title-section">
            <input
              type="text"
              className="chapter-title-input"
              value={bookTitle}
              onChange={(e) => setBookTitle(e.target.value)}
              placeholder="Bölüm başlığını giriniz..."
            />
          </div>

          {/* Combined Toolbar and Word Count */}
          <div className="editor-meta-bar">
            <div className="formatting-toolbar">
              <div className="format-group">
                <button
                  className={`format-btn ${isBold ? "active" : ""}`}
                  onClick={toggleBold}
                  title="Kalın"
                >
                  <strong>B</strong>
                </button>
                <button
                  className={`format-btn ${isItalic ? "active" : ""}`}
                  onClick={toggleItalic}
                  title="İtalik"
                >
                  <em>I</em>
                </button>
                <button
                  className={`format-btn ${isStrikethrough ? "active" : ""}`}
                  onClick={toggleStrikethrough}
                  title="Üstü Çizili"
                >
                  <s>S</s>
                </button>
              </div>
            </div>
            <div className="editor-stats">
                <span>Kelime: {wordCount}</span>
            </div>
          </div>

          <div
            ref={editorRef}
            className="story-editor"
            contentEditable
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            data-placeholder="Hikayenizi yazmaya başlayın..."
          ></div>
        </div>
      </main>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{isPublished ? "Yayımdan Kaldır" : "Yayımla"}</h2>
            </div>
            <div className="modal-body">
              <p>
                {isPublished
                  ? "Bu bölümü yayımdan kaldırmak istediğinize emin misiniz?"
                  : "Bu bölümü yayımlamak istediğinize emin misiniz?"}
              </p>
            </div>
            <div className="modal-actions">
              <button onClick={() => setIsModalOpen(false)} className="modal-btn cancel-btn">
                Vazgeç
              </button>
              <button onClick={handleConfirmPublish} className="modal-btn confirm-btn">
                {isPublished ? "Kaldır" : "Yayımla"}
              </button>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="success-toast">
          ✅ <span>{successMessage}</span>
        </div>
      )}
    </div>
  );
}